import { IFieldConfig } from '@root/Components/Recommendations/RecommendationGrid';
import { useDi } from '@root/Services/DI';
import { FormatService } from '@root/Services/FormatService';
import { endpoint } from '@root/Services/Router/EndpointRegistry';
import { useMemo } from 'react';
import { IComparsonConfig, IMetricInfo } from '@root/Components/Recommendations/Options/Models';
import { BaseRightsizingPage, SelectionSpecCompare } from './Components';
import { Box, Group } from '@mantine/core';
import { ChevronRight } from 'tabler-icons-react';
import { ConnectionCheck } from '@root/Components/Resources/ConnectionCheck';

const specMetrics = [
    {
        label: 'vCPUs',
        path: 'vCpu',
        value: (o) => o['vCpu'] ?? -1,
        format: (v) => (v < 0 ? 'Not Available' : v),
    },
    {
        label: 'Memory',
        path: 'Memory',
        value: (o) => o['Memory'] ?? -1,
        format: (v) => (v < 0 ? 'Not Available' : v + 'GB'),
    },
    {
        label: 'Network Performance',
        path: 'NetworkPerformance',
        value: (o) => o['NetworkPerformance'] ?? -1,
        format: (v) => (v < 0 ? 'Not Available' : v.toFixed(v < 1 ? 1 : 0) + 'Gbps'),
    },
] as IMetricInfo[];

export function RdsRightsizingPage() {
    const fmtSvc = useDi(FormatService);
    const fieldConfig = useMemo(
        () =>
            ({
                selectorRenderer: (r) => {
                    if (!r.OptionSelected?.ChangeDetails?.SizeChange?.RecommendedSize) {
                        return <>No Change</>;
                    } else {
                        const {
                            InstanceTypeAttributes: { InstanceType: currentType },
                        } = r.Factors as { InstanceTypeAttributes: { InstanceType: string } };
                        const selectedType = r.OptionSelected?.ChangeDetails?.SizeChange?.RecommendedSize;
                        return (
                            <>
                                {!selectedType ? (
                                    'No Change'
                                ) : (
                                    <Box ml="xs">
                                        <Group spacing="sm" sx={{ height: 25 }} noWrap>
                                            {currentType} <ChevronRight size={16} /> {selectedType}
                                        </Group>
                                        <SelectionSpecCompare specMetrics={specMetrics} recommendation={r} />
                                    </Box>
                                )}
                            </>
                        );
                    }
                },
                currentStateField: 'Factors.InstanceTypeAttributes.InstanceType',
                currentStateFieldLabel: 'Instance Type',
                currentStateAccessor: (r) => (r.Factors as any)?.InstanceTypeAttributes?.InstanceType ?? '',
                factorFields: [
                    'Factors.AvailabilityZone',
                    'Factors.FromCloudFormation',
                    'Factors.UsedByEMR',
                    'Factors.UsedByEKS',
                    'Factors.UsedByElasticBeanstalk',
                    'Factors.UsedInAutoScaleGroup',
                    'Factors.ClusterName',
                    'Factors.HasParentResource',
                    'Factors.InstanceLifecycle',
                    'Factors.HasSavingsPlanCoveredUsage',
                    'Factors.HasDiscountedUsage',
                    'Factors.InstanceTypeAttributes.Sku',
                    'Factors.InstanceTypeAttributes.Location',
                    'Factors.InstanceTypeAttributes.LocationType',
                    'Factors.InstanceTypeAttributes.InstanceType',
                    'Factors.InstanceTypeAttributes.CurrentGeneration',
                    'Factors.InstanceTypeAttributes.InstanceFamily',
                    'Factors.InstanceTypeAttributes.vCpu',
                    'Factors.InstanceTypeAttributes.PhysicalProcessor',
                    'Factors.InstanceTypeAttributes.Memory',
                    'Factors.InstanceTypeAttributes.Storage',
                    'Factors.InstanceTypeAttributes.NetworkPerformance',
                    'Factors.InstanceTypeAttributes.ProcessorArchitecture',
                    'Factors.InstanceTypeAttributes.EngineCode',
                    'Factors.InstanceTypeAttributes.DatabaseEngine',
                    'Factors.InstanceTypeAttributes.DatabaseEdition',
                    'Factors.InstanceTypeAttributes.LicenseModel',
                    'Factors.InstanceTypeAttributes.DeploymentOption',
                    'Factors.InstanceTypeAttributes.UsageType',
                    'Factors.InstanceTypeAttributes.Operation',
                    'Factors.InstanceTypeAttributes.InstanceTypeFamily',
                    'Factors.InstanceTypeAttributes.NormalizationSizeFactor',
                    'Factors.Pricing.PricePerUnit',
                    'Factors.AllocatedStorage',
                    'Factors.Metrics.CpuUtilization',
                    'Factors.Metrics.NetworkIn',
                    'Factors.Metrics.NetworkOut',
                    'Factors.Metrics.DatabaseConnections',
                    'Factors.Metrics.ReadIOPS',
                    'Factors.Metrics.WriteIOPS',
                    'Factors.Metrics.SwapUsage',
                    'Factors.Metrics.FreeableMemory',
                    'Factors.Metrics.FreeStorageSpace',
                ],
                columnConfig: {
                    'Factors.Metrics.CpuUtilization': {
                        header: 'CPU Utilization',
                        helpText: 'Top Average CloudWatch CPU for last 30 days per 1 hour interval.',
                        format: 'whole-percent',
                    },
                    'Factors.Metrics.NetworkIn': {
                        header: 'Network In',
                        helpText: 'Top Average CloudWatch Network In for last 30 days per 1 hour interval.',
                        format: 'bytes',
                    },
                    'Factors.Metrics.NetworkOut': {
                        header: 'Network Out',
                        helpText: 'Top Average CloudWatch Network Out for last 30 days per 1 hour interval.',
                        format: 'bytes',
                    },
                    'Factors.Metrics.DatabaseConnections': {
                        header: 'Database Connections',
                        helpText: 'Top Count of CloudWatch Database Connections for last 30 days.',
                        format: 'number-with-two-decimals',
                    },
                    'Factors.Metrics.ReadIOPS': {
                        header: 'Read IOPS',
                        helpText: 'Top Average CloudWatch Read IOPS for last 30 days per 1 hour interval.',
                        format: 'number-with-two-decimals',
                    },
                    'Factors.Metrics.WriteIOPS': {
                        header: 'Write IOPS',
                        helpText: 'Top Average CloudWatch Write IOPS for last 30 days per 1 hour interval.',
                        format: 'number-with-two-decimals',
                    },
                    'Factors.Metrics.SwapUsage': {
                        header: 'Swap Usage',
                        helpText: 'Top Average CloudWatch Swap Usage Usage for last 30 days per 1 hour interval.',
                        format: 'bytes',
                    },
                    'Factors.Metrics.FreeableMemory': {
                        header: 'Freeable Memory',
                        helpText: 'Top Average CloudWatch Freeable Memory for last 30 days per 1 hour interval.',
                        format: 'bytes',
                    },
                    'Factors.Metrics.FreeStorageSpace': {
                        header: 'Free Storage Space',
                        helpText: 'Top Average CloudWatch Free Storage Space for last 30 days per 1 hour interval.',
                        format: 'bytes',
                    },
                    'Factors.ClusterName': {
                        header: 'Cluster Name',
                        helpText: 'The name of the cluster that the instance is in.',
                        format: 'string',
                    },
                    'Factors.InstanceTypeAttributes.vCpu': {
                        header: 'vCPUs',
                        helpText: 'The number of virtual CPUs that the instance has.',
                        format: 'number',
                    },
                },
                defaultColumns: [
                    'LowestRiskHighestSavings.ExpectedAnnualSavings',
                    'LowestRiskHighestSavings.ChangeDetails.SizeChange.RecommendedSize',
                    'Factors.InstanceTypeAttributes.InstanceType',
                    'ResourceMetadata.Account',
                    'ResourceMetadata.AccountName',
                    'ResourceMetadata.Name',
                    'ResourceMetadata.ResourceId',
                    'ResourceMetadata.ExtendedAnnualizedCost',
                    'Factors.InstanceTypeAttributes.DatabaseEngine',
                    'ResourceMetadata.Region',
                    'Factors.ClusterName',
                ],
                specInfo: specMetrics,
            } as IFieldConfig),
        []
    );

    const comparisonConfig = useMemo(
        () =>
            ({
                currentStateAccessor: (o) => (o.Factors as any)?.InstanceTypeAttributes?.InstanceType ?? 'Unknown',
                observabilityMetrics: [
                    {
                        metric: 'CPUUtilization',
                        spec: 'vCpu',
                        title: 'CPU',
                        transform: (v) => v / 100,
                    },
                    {
                        metric: 'NetworkReceiveThroughput',
                        spec: 'NetworkPerformance',
                        title: 'Network In',
                        transform: (v, r) => v / ((r.HistoricalUtilizationMetrics?.['NetworkPerformance'] ?? 1) * 1024 ** 3),
                    },
                    {
                        metric: 'NetworkTransmitThroughput',
                        spec: 'NetworkPerformance',
                        title: 'Network Out',
                        transform: (v, r) => v / ((r.HistoricalUtilizationMetrics?.['NetworkPerformance'] ?? 1) * 1024 ** 3),
                    },
                    {
                        metric: 'FreeableMemory',
                        spec: 'Memory',
                        title: 'Freeable Memory',
                        format: 'bytes',
                        yMax: -1,
                        transform: (v, r) => v,
                        prerenderTransform: (v, r, targetSpec) =>
                            Math.max(0, targetSpec * 1024 ** 3 - ((r.HistoricalUtilizationMetrics?.['Memory'] ?? 1) * 1024 ** 3 - v)),
                    },
                ],
                utilizationMetrics: [
                    {
                        label: 'CPU Utilization',
                        value: (o) => (o['CpuUtilization'] ?? 0) / 100,
                        format: (v) => (v < 0 ? 'Not Available' : fmtSvc.formatPercent(v)),
                    },
                    {
                        label: 'Network In',
                        value: (o) => o['NetworkIn'] ?? -1,
                        format: (v) => (v < 0 ? 'Not Available' : fmtSvc.formatPercent(v)),
                    },
                    {
                        label: 'Network Out',
                        value: (o) => o['NetworkOut'] ?? -1,
                        format: (v) => (v < 0 ? 'Not Available' : fmtSvc.formatPercent(v)),
                    },
                    {
                        label: 'Freeable Memory',
                        value: (o) => o['FreeableMemory'] ?? -1,
                        format: (v) => (v < 0 ? 'Not Available' : fmtSvc.formatBytes(v, null)),
                        preferDecrease: false,
                    },
                ],
                specMetrics: specMetrics,
            } as IComparsonConfig),
        []
    );

    return (
        <ConnectionCheck>
            {() => <BaseRightsizingPage title="RDS" comparisonConfig={comparisonConfig} fieldConfig={fieldConfig} resourceType="RDS" />}
        </ConnectionCheck>
    );
}

endpoint('rightsizing-rds', RdsRightsizingPage, 'Rightsizing');
